import React, { MouseEventHandler, ReactNode } from 'react';
import clsx from 'clsx';

export type ButtonProps = {
  key?: string;
  title?: string;
  children?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  className?: string;
  disabled?: boolean;
  type?: 'button' | 'submit' | 'reset';
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  iconClassName?: string;
  iconOnly?: boolean;
  ariaLabel?: string;
};

function Button({
  title,
  children,
  onClick,
  className = '',
  disabled = false,
  type = 'button',
  startIcon,
  endIcon,
  iconClassName = '',
  iconOnly = false,
  ariaLabel,
}: ButtonProps) {
  const content = children ?? title ?? '';

  const baseClassName = `
    flex items-center justify-center gap-2 
    rounded-none border border-gray-400 
    bg-gradient-to-b from-gray-100 to-gray-300 
    px-4 py-2 text-sm font-semibold text-gray-800 shadow-sm
  `;

  const interactiveClassName = `
    hover:from-gray-200 hover:to-gray-400
    active:from-gray-300 active:to-gray-500 active:shadow-inner
    disabled:cursor-not-allowed disabled:from-gray-100 disabled:to-gray-100 
    disabled:text-gray-400 disabled:shadow-none
  `;

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={
        iconOnly ? (ariaLabel ?? (typeof content === 'string' ? content : 'Button')) : undefined
      }
      className={clsx(baseClassName, interactiveClassName, className)}
    >
      {startIcon && <span className={iconClassName}>{startIcon}</span>}
      {!iconOnly && <span>{content}</span>}
      {endIcon && <span className={iconClassName}>{endIcon}</span>}
    </button>
  );
}

export default Button;
